
/**
 * Color utility functions for the theme management system
 */

// Check if a string is a valid hex color
export const isValidHex = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

// Convert hex to RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse the hex values
  let r, g, b;
  
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  
  return { r, g, b };
};

// Convert RGB to hex
export const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b]
    .map(c => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0'))
    .join('');
};

// Parse RGB string to components
export const parseRgb = (rgbStr: string): { r: number; g: number; b: number } => {
  const match = rgbStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) throw new Error('Invalid RGB format');
  
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10)
  };
};

// Convert color from one format to another
export const convertColorFormat = (
  color: string,
  fromFormat: 'hex' | 'rgb' | 'hsl',
  toFormat: 'hex' | 'rgb' | 'hsl'
): string => {
  // If formats are the same, no conversion needed
  if (fromFormat === toFormat) return color;
  
  // Handle specific conversions
  if (fromFormat === 'hex' && toFormat === 'rgb') {
    const { r, g, b } = hexToRgb(color);
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  if (fromFormat === 'rgb' && toFormat === 'hex') {
    const { r, g, b } = parseRgb(color);
    return rgbToHex(r, g, b);
  }
  
  // Add more conversions as needed (HSL, etc.)
  
  // For now, return original if conversion not supported
  return color;
};

// Calculate relative luminance for WCAG contrast calculations
const calculateRelativeLuminance = (color: string): number => {
  // Convert to RGB first if it's a hex color
  const rgb = color.startsWith('#') ? hexToRgb(color) : parseRgb(color);
  
  // Normalize RGB values
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  
  // Calculate luminance components
  const R = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const G = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const B = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate luminance using the formula
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

// Calculate contrast ratio between two colors
export const calculateContrastRatio = (color1: string, color2: string): number => {
  const luminance1 = calculateRelativeLuminance(color1);
  const luminance2 = calculateRelativeLuminance(color2);
  
  // Determine which luminance is lighter and which is darker
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  
  // Calculate contrast ratio using the WCAG formula
  return (lighter + 0.05) / (darker + 0.05);
};
